import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Tabs,
  Table,
  Input,
  Button,
  Upload,
  Popconfirm,
  Select,
  Tag,
  Tooltip,
  notification,
  Space,
  Modal,
  Badge,
  Empty,
  Spin
} from "antd";
import {
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileUnknownOutlined,
  UploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FolderOpenOutlined,
  GlobalOutlined
} from "@ant-design/icons";
import { getReq, postReq, deleteApi } from "../api";
import "./DmsPage.css";

const { Title, Text } = Typography;
const { Option } = Select;

export default function DmsPage() {
  const currentUser = JSON.parse(localStorage.getItem("userDetails") || '{"name": "Admin User"}');

  // States
  const [clients, setClients] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [companyDocs, setCompanyDocs] = useState([]);
  const [clientDocs, setClientDocs] = useState([]);
  const [candidateDocs, setCandidateDocs] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);

  // Search Filter State (Company tab)
  const [companySearch, setCompanySearch] = useState("");


  // Loading States
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [loadingClient, setLoadingClient] = useState(false);
  const [loadingCandidate, setLoadingCandidate] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Modal Visibility States
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [candidateModalOpen, setCandidateModalOpen] = useState(false);

  // Form values - Company Modal
  const [companyDocName, setCompanyDocName] = useState("");
  const [companyFile, setCompanyFile] = useState(null);
  const [companyFileList, setCompanyFileList] = useState([]);

  // Form values - Client Modal
  const [clientDocName, setClientDocName] = useState("");
  const [clientFile, setClientFile] = useState(null);
  const [clientFileList, setClientFileList] = useState([]);

  // Form values - Candidate Modal
  const [candidateDocName, setCandidateDocName] = useState("");
  const [candidateFile, setCandidateFile] = useState(null);
  const [candidateFileList, setCandidateFileList] = useState([]);

  // Fetch all dropdown clients
  const fetchClients = async () => {
    setLoadingClients(true);
    const userRole = localStorage.getItem("userRole");
    const endpoint = userRole === "company" ? "company/clients/dropdown" : "common/clients/dropdown";
    try {
      const res = await postReq(endpoint, {});
      if (res && res.status === 200) {
        // Map from { value, label } structure returned by dropdown endpoint
        setClients(res.data || []);
      } else {
        notification.error({
          message: "Error",
          description: "Failed to load clients.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error("Failed to load clients:", err);
    } finally {
      setLoadingClients(false);
    }
  };

  // Fetch all dropdown candidates
  const fetchCandidates = async () => {
    setLoadingCandidates(true);
    const userRole = localStorage.getItem("userRole");
    const endpoint = userRole === "company" ? "company/candidate/dropdown" : "common/candidate/dropdown";
    try {
      const res = await postReq(endpoint, {});
      if (res && res.status === 200) {
        setCandidates(res.data || []);
      } else {
        notification.error({
          message: "Error",
          description: "Failed to load candidates.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error("Failed to load candidates:", err);
    } finally {
      setLoadingCandidates(false);
    }
  };

  // Fetch corporate documents
  const fetchCompanyDocs = async () => {
    setLoadingCompany(true);
    try {
      const res = await getReq("dms?category=company");
      if (res && res.status === 200) {
        setCompanyDocs(res.data || []);
      }
    } catch (err) {
      console.error("Failed to load company documents:", err);
    } finally {
      setLoadingCompany(false);
    }
  };

  // Fetch client documents
  const fetchClientDocs = async (clientId) => {
    if (!clientId) return;
    setLoadingClient(true);
    try {
      const res = await getReq(`dms?category=client&clientId=${clientId}`);
      if (res && res.status === 200) {
        setClientDocs(res.data || []);
      }
    } catch (err) {
      console.error("Failed to load client documents:", err);
    } finally {
      setLoadingClient(false);
    }
  };

  // Fetch candidate documents
  const fetchCandidateDocs = async (candidateId) => {
    if (!candidateId) return;
    setLoadingCandidate(true);
    try {
      const res = await getReq(`dms?category=candidate&candidateId=${candidateId}`);
      if (res && res.status === 200) {
        setCandidateDocs(res.data || []);
      }
    } catch (err) {
      console.error("Failed to load candidate documents:", err);
    } finally {
      setLoadingCandidate(false);
    }
  };

  // Effects
  useEffect(() => {
    fetchClients();
    fetchCandidates();
    fetchCompanyDocs();
  }, []);

  useEffect(() => {
    if (selectedClientId) {
      fetchClientDocs(selectedClientId);
    } else {
      setClientDocs([]);
    }
  }, [selectedClientId]);

  useEffect(() => {
    if (selectedCandidateId) {
      fetchCandidateDocs(selectedCandidateId);
    } else {
      setCandidateDocs([]);
    }
  }, [selectedCandidateId]);

  // Helper formatting logic
  const getFileIcon = (fileType) => {
    const type = fileType?.toLowerCase() || "";
    if (type.includes("pdf")) {
      return <span className="doc-icon-wrapper doc-icon-pdf"><FilePdfOutlined /></span>;
    }
    if (type.includes("word") || type.includes("document") || type.includes("docx") || type.includes("doc")) {
      return <span className="doc-icon-wrapper doc-icon-word"><FileWordOutlined /></span>;
    }
    if (type.includes("sheet") || type.includes("excel") || type.includes("spreadsheet") || type.includes("xlsx") || type.includes("xls")) {
      return <span className="doc-icon-wrapper doc-icon-excel"><FileExcelOutlined /></span>;
    }
    return <span className="doc-icon-wrapper doc-icon-other"><FileUnknownOutlined /></span>;
  };

  const formatSize = (bytes) => {
    if (!bytes) return "—";
    if (bytes >= 1048576) {
      return `${(bytes / 1048576).toFixed(2)} MB`;
    }
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  // Live download opener
  const handleDownload = (doc) => {
    const apiUrl = process.env.REACT_APP_API_URL || "";
    let downloadUrl = "";
    if (apiUrl.includes("localhost")) {
      downloadUrl = `http://localhost:9003/files/${doc.fileName}`;
    } else {
      downloadUrl = `https://rmt.cloudstakes.com/api/files/${doc.fileName}`;
    }
    window.open(downloadUrl, "_blank");
    notification.success({
      message: "Downloading File",
      description: `Opening ${doc.fileName} in new tab.`,
      placement: "topRight"
    });
  };

  // Upload Actions
  const handleUploadCompany = async () => {
    if (!companyDocName.trim()) {
      notification.warning({
        message: "Details Missing",
        description: "Please enter a document display name.",
        placement: "topRight"
      });
      return;
    }
    if (!companyFile) {
      notification.warning({
        message: "No File Selected",
        description: "Please select a file to upload.",
        placement: "topRight"
      });
      return;
    }

    const formData = new FormData();
    formData.append("name", companyDocName.trim());
    formData.append("category", "company");
    formData.append("file", companyFile);

    setUploading(true);
    try {
      const res = await postReq("dms/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      if (res && res.status === 200) {
        notification.success({
          message: "Upload Successful",
          description: `"${companyDocName}" has been added to Company Documents.`,
          placement: "topRight"
        });
        setCompanyDocName("");
        setCompanyFile(null);
        setCompanyFileList([]);
        setCompanyModalOpen(false);
        fetchCompanyDocs();
      } else {
        notification.error({
          message: "Upload Failed",
          description: res.message || "Failed to upload document.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Upload Failed",
        description: "Could not connect to server.",
        placement: "topRight"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadClient = async () => {
    if (!selectedClientId) {
      notification.error({
        message: "Error",
        description: "Please select a client before uploading.",
        placement: "topRight"
      });
      return;
    }
    if (!clientDocName.trim()) {
      notification.warning({
        message: "Details Missing",
        description: "Please enter a document display name.",
        placement: "topRight"
      });
      return;
    }
    if (!clientFile) {
      notification.warning({
        message: "No File Selected",
        description: "Please select a file to upload.",
        placement: "topRight"
      });
      return;
    }

    const formData = new FormData();
    formData.append("name", clientDocName.trim());
    formData.append("category", "client");
    formData.append("clientId", selectedClientId);
    formData.append("file", clientFile);

    setUploading(true);
    try {
      const res = await postReq("dms/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      if (res && res.status === 200) {
        notification.success({
          message: "Upload Successful",
          description: `"${clientDocName}" has been added for this client.`,
          placement: "topRight"
        });
        setClientDocName("");
        setClientFile(null);
        setClientFileList([]);
        setClientModalOpen(false);
        fetchClientDocs(selectedClientId);
      } else {
        notification.error({
          message: "Upload Failed",
          description: res.message || "Failed to upload document.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Upload Failed",
        description: "Could not connect to server.",
        placement: "topRight"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadCandidate = async () => {
    if (!selectedCandidateId) {
      notification.error({
        message: "Error",
        description: "Please select a candidate before uploading.",
        placement: "topRight"
      });
      return;
    }
    if (!candidateDocName.trim()) {
      notification.warning({
        message: "Details Missing",
        description: "Please enter a document display name.",
        placement: "topRight"
      });
      return;
    }
    if (!candidateFile) {
      notification.warning({
        message: "No File Selected",
        description: "Please select a file to upload.",
        placement: "topRight"
      });
      return;
    }

    const formData = new FormData();
    formData.append("name", candidateDocName.trim());
    formData.append("category", "candidate");
    formData.append("candidateId", selectedCandidateId);
    formData.append("file", candidateFile);

    setUploading(true);
    try {
      const res = await postReq("dms/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      if (res && res.status === 200) {
        notification.success({
          message: "Upload Successful",
          description: `"${candidateDocName}" has been added for this candidate.`,
          placement: "topRight"
        });
        setCandidateDocName("");
        setCandidateFile(null);
        setCandidateFileList([]);
        setCandidateModalOpen(false);
        fetchCandidateDocs(selectedCandidateId);
      } else {
        notification.error({
          message: "Upload Failed",
          description: res.message || "Failed to upload document.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Upload Failed",
        description: "Could not connect to server.",
        placement: "topRight"
      });
    } finally {
      setUploading(false);
    }
  };

  // Delete Actions
  const handleDeleteCompany = async (id) => {
    try {
      const res = await deleteApi(`dms/${id}`);
      if (res && (res.status === 200 || res.data?.success)) {
        notification.success({
          message: "Document Deleted",
          description: "Company document removed successfully.",
          placement: "topRight"
        });
        fetchCompanyDocs();
      } else {
        notification.error({
          message: "Delete Failed",
          description: res.message || "Could not delete document.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Delete Failed",
        description: "Could not connect to server.",
        placement: "topRight"
      });
    }
  };

  const handleDeleteClient = async (id) => {
    try {
      const res = await deleteApi(`dms/${id}`);
      if (res && (res.status === 200 || res.data?.success)) {
        notification.success({
          message: "Document Deleted",
          description: "Client document removed successfully.",
          placement: "topRight"
        });
        fetchClientDocs(selectedClientId);
      } else {
        notification.error({
          message: "Delete Failed",
          description: res.message || "Could not delete document.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Delete Failed",
        description: "Could not connect to server.",
        placement: "topRight"
      });
    }
  };

  const handleDeleteCandidate = async (id) => {
    try {
      const res = await deleteApi(`dms/${id}`);
      if (res && (res.status === 200 || res.data?.success)) {
        notification.success({
          message: "Document Deleted",
          description: "Candidate document removed successfully.",
          placement: "topRight"
        });
        fetchCandidateDocs(selectedCandidateId);
      } else {
        notification.error({
          message: "Delete Failed",
          description: res.message || "Could not delete document.",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Delete Failed",
        description: "Could not connect to server.",
        placement: "topRight"
      });
    }
  };

  const activeClientInfo = clients.find((c) => c.value === selectedClientId);
  const activeCandidateInfo = candidates.find((c) => c.value === selectedCandidateId);

  // Filter company documents list
  const filteredCompanyDocs = companyDocs.filter(
    (d) =>
      d.name.toLowerCase().includes(companySearch.toLowerCase()) ||
      d.fileName.toLowerCase().includes(companySearch.toLowerCase())
  );

  // Table Columns
  const companyColumns = [
    {
      title: "Document Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <span>
          {getFileIcon(record.fileType || record.fileName)}
          <Text strong style={{ color: "#334155" }}>{text}</Text>
        </span>
      )
    },
    {
      title: "Original Filename",
      dataIndex: "fileName",
      key: "fileName",
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: "Size",
      dataIndex: "fileSize",
      key: "fileSize",
      render: (size) => formatSize(size)
    },
    {
      title: "Uploaded By",
      dataIndex: "uploadedBy",
      key: "uploadedBy",
      render: (uploader) => <Tag color="blue">{uploader?.name || "Unknown"}</Tag>
    },
    {
      title: "Date Added",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => formatDate(date)
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Download File">
            <Button
              type="text"
              icon={<DownloadOutlined style={{ color: "#13487d" }} />}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure to delete this document?"
            onConfirm={() => handleDeleteCompany(record._id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete File">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const clientColumns = [
    {
      title: "Document Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <span>
          {getFileIcon(record.fileType || record.fileName)}
          <Text strong style={{ color: "#334155" }}>{text}</Text>
        </span>
      )
    },
    {
      title: "Original Filename",
      dataIndex: "fileName",
      key: "fileName",
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: "Size",
      dataIndex: "fileSize",
      key: "fileSize",
      render: (size) => formatSize(size)
    },
    {
      title: "Uploaded By",
      dataIndex: "uploadedBy",
      key: "uploadedBy",
      render: (uploader) => <Tag color="cyan">{uploader?.name || "Unknown"}</Tag>
    },
    {
      title: "Date Added",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => formatDate(date)
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Download File">
            <Button
              type="text"
              icon={<DownloadOutlined style={{ color: "#13487d" }} />}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure to delete this document?"
            onConfirm={() => handleDeleteClient(record._id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete File">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const candidateColumns = [
    {
      title: "Document Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <span>
          {getFileIcon(record.fileType || record.fileName)}
          <Text strong style={{ color: "#334155" }}>{text}</Text>
        </span>
      )
    },
    {
      title: "Original Filename",
      dataIndex: "fileName",
      key: "fileName",
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: "Size",
      dataIndex: "fileSize",
      key: "fileSize",
      render: (size) => formatSize(size)
    },
    {
      title: "Uploaded By",
      dataIndex: "uploadedBy",
      key: "uploadedBy",
      render: (uploader) => <Tag color="cyan">{uploader?.name || "Unknown"}</Tag>
    },
    {
      title: "Date Added",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => formatDate(date)
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Download File">
            <Button
              type="text"
              icon={<DownloadOutlined style={{ color: "#13487d" }} />}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure to delete this document?"
            onConfirm={() => handleDeleteCandidate(record._id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete File">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="dms-container">
      <div className="dms-card">
        {/* Header */}
        <div className="dms-header">
          <div>
            <Title className="dms-header-title">Document Management System</Title>
            <span className="dms-header-subtitle">
              Securely store, organize, and download corporate policy documents and client contracts
            </span>
          </div>
        </div>

        {/* Tab Wrapper */}
        <Tabs defaultActiveKey="1" className="dms-tabs">
          {/* Tab 1: Company Documents */}
          <Tabs.TabPane 
            tab={
              <span>
                Company Documents{" "}
                <Badge 
                  count={companyDocs.length} 
                  style={{ backgroundColor: "#13487d", marginLeft: 6 }} 
                  showZero 
                />
              </span>
            } 
            key="1"
          >
            <div style={{ padding: "0 24px 24px 24px" }}>
              
              {/* Controls Row: Search + Upload Trigger */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <Input
                  placeholder="Search corporate documents by name..."
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  style={{ width: 320 }}
                  allowClear
                />
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => setCompanyModalOpen(true)}
                  className="btn-premium"
                >
                  Upload Corporate Document
                </Button>
              </div>

              {/* Table List */}
              <div className="dms-table-container">
                <Table
                  dataSource={filteredCompanyDocs}
                  columns={companyColumns}
                  rowKey="_id"
                  className="dms-table"
                  pagination={{ pageSize: 8 }}
                  loading={loadingCompany}
                />
              </div>
            </div>
          </Tabs.TabPane>

          {/* Tab 2: Client Documents */}
          <Tabs.TabPane 
            tab={
              <span>
                Client Documents{" "}
                <Badge 
                  count={clientDocs.length} 
                  style={{ backgroundColor: "#64748b", marginLeft: 6 }} 
                  showZero 
                />
              </span>
            } 
            key="2"
          >
            <div style={{ padding: "0 24px 24px 24px" }}>
              {/* Client Dropdown Bar */}
              <div className="dms-client-selector-bar">
                <span className="dms-client-label">Select Workspace Client:</span>
                <Select
                  showSearch
                  placeholder="Search and select client company..."
                  optionFilterProp="children"
                  className="dms-client-select"
                  onChange={(val) => {
                    setSelectedClientId(val);
                    setClientDocName("");
                    setClientFile(null);
                    setClientFileList([]);
                  }}
                  value={selectedClientId}
                  loading={loadingClients}
                >
                  {clients.map((c) => (
                    <Option key={c.value} value={c.value}>
                      {c.label}
                    </Option>
                  ))}
                </Select>
              </div>

              {/* Main Content Area */}
              {!selectedClientId ? (
                <div className="dms-empty-state">
                  <div className="dms-empty-icon-container">
                    <FolderOpenOutlined />
                  </div>
                  <div className="dms-empty-title">No Client Selected</div>
                  <p>Choose a client company from the workspace dropdown above to view or upload documents.</p>
                </div>
              ) : (
                <div>
                  {/* Client Info Banner */}
                  <div className="client-info-header">
                    <div>
                      <h4 className="client-info-name">{activeClientInfo?.label}</h4>
                      <div className="client-info-meta">
                        <span>
                          <GlobalOutlined style={{ marginRight: 4 }} />
                          <a href={`https://${activeClientInfo?.website}`} target="_blank" rel="noreferrer">
                            {activeClientInfo?.website || "Website"}
                          </a>
                        </span>
                      </div>
                    </div>
                    <Space size="middle">
                      <Tag color="purple" style={{ margin: 0, padding: "4px 10px", fontSize: "13px", fontWeight: 600 }}>
                        {clientDocs.length} Documents
                      </Tag>
                      <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={() => setClientModalOpen(true)}
                        className="btn-premium"
                      >
                        Upload Client Document
                      </Button>
                    </Space>
                  </div>

                  {/* Document Table */}
                  {clientDocs.length === 0 && !loadingClient ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={`No documents uploaded for ${activeClientInfo?.label} yet.`}
                      style={{ margin: "48px 0" }}
                    />
                  ) : (
                    <div className="dms-table-container">
                      <Table
                        dataSource={clientDocs}
                        columns={clientColumns}
                        rowKey="_id"
                        className="dms-table"
                        pagination={{ pageSize: 8 }}
                        loading={loadingClient}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </Tabs.TabPane>

          {/* Tab 3: Candidate Documents */}
          <Tabs.TabPane 
            tab={
              <span>
                Candidate Documents{" "}
                <Badge 
                  count={candidateDocs.length} 
                  style={{ backgroundColor: "#2563eb", marginLeft: 6 }} 
                  showZero 
                />
              </span>
            } 
            key="3"
          >
            <div style={{ padding: "0 24px 24px 24px" }}>
              {/* Candidate Dropdown Bar */}
              <div className="dms-client-selector-bar">
                <span className="dms-client-label">Select Candidate:</span>
                <Select
                  showSearch
                  placeholder="Search and select candidate..."
                  optionFilterProp="children"
                  className="dms-client-select"
                  onChange={(val) => {
                    setSelectedCandidateId(val);
                    setCandidateDocName("");
                    setCandidateFile(null);
                    setCandidateFileList([]);
                  }}
                  value={selectedCandidateId}
                  loading={loadingCandidates}
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {candidates.map((c) => (
                    <Option key={c.value} value={c.value}>
                      {c.label}
                    </Option>
                  ))}
                </Select>
              </div>

              {/* Main Content Area */}
              {!selectedCandidateId ? (
                <div className="dms-empty-state">
                  <div className="dms-empty-icon-container">
                    <FolderOpenOutlined />
                  </div>
                  <div className="dms-empty-title">No Candidate Selected</div>
                  <p>Choose a candidate from the workspace dropdown above to view or upload documents.</p>
                </div>
              ) : (
                <div>
                  {/* Candidate Info Banner */}
                  <div className="client-info-header">
                    <div>
                      <h4 className="client-info-name">{activeCandidateInfo?.label}</h4>
                    </div>
                    <Space size="middle">
                      <Tag color="purple" style={{ margin: 0, padding: "4px 10px", fontSize: "13px", fontWeight: 600 }}>
                        {candidateDocs.length} Documents
                      </Tag>
                      <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={() => setCandidateModalOpen(true)}
                        className="btn-premium"
                      >
                        Upload Candidate Document
                      </Button>
                    </Space>
                  </div>

                  {/* Document Table */}
                  {candidateDocs.length === 0 && !loadingCandidate ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={`No documents uploaded for ${activeCandidateInfo?.label} yet.`}
                      style={{ margin: "48px 0" }}
                    />
                  ) : (
                    <div className="dms-table-container">
                      <Table
                        dataSource={candidateDocs}
                        columns={candidateColumns}
                        rowKey="_id"
                        className="dms-table"
                        pagination={{ pageSize: 8 }}
                        loading={loadingCandidate}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </Tabs.TabPane>
        </Tabs>

        {/* ── Modals ── */}
        
        {/* Company Upload Modal */}
        <Modal
          title={<span style={{ fontWeight: 700, fontSize: "16px", color: "#1e293b" }}>Upload Corporate Document</span>}
          open={companyModalOpen}
          onCancel={() => {
            setCompanyModalOpen(false);
            setCompanyDocName("");
            setCompanyFile(null);
            setCompanyFileList([]);
          }}
          onOk={handleUploadCompany}
          okText="Upload"
          cancelText="Cancel"
          okButtonProps={{ className: "btn-premium", loading: uploading }}
          destroyOnClose
        >
          <div style={{ padding: "12px 0" }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500, color: "#475569", fontSize: "13px" }}>
                Document Display Name
              </label>
              <Input
                placeholder="e.g. Employee Handbook 2026"
                value={companyDocName}
                onChange={(e) => setCompanyDocName(e.target.value)}
                disabled={uploading}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500, color: "#475569", fontSize: "13px" }}>
                Select File
              </label>
              <Upload
                fileList={companyFileList}
                beforeUpload={(file) => {
                  setCompanyFile(file);
                  setCompanyFileList([file]);
                  return false;
                }}
                onRemove={() => {
                  setCompanyFile(null);
                  setCompanyFileList([]);
                }}
                maxCount={1}
                disabled={uploading}
              >
                <Button icon={<UploadOutlined />} disabled={uploading}>Choose File</Button>
              </Upload>
            </div>
          </div>
        </Modal>

        {/* Client Upload Modal */}
        <Modal
          title={
            <span style={{ fontWeight: 700, fontSize: "16px", color: "#1e293b" }}>
              Upload Document for {activeClientInfo?.label}
            </span>
          }
          open={clientModalOpen}
          onCancel={() => {
            setClientModalOpen(false);
            setClientDocName("");
            setClientFile(null);
            setClientFileList([]);
          }}
          onOk={handleUploadClient}
          okText="Upload"
          cancelText="Cancel"
          okButtonProps={{ className: "btn-premium", loading: uploading }}
          destroyOnClose
        >
          <div style={{ padding: "12px 0" }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500, color: "#475569", fontSize: "13px" }}>
                Document Display Name
              </label>
              <Input
                placeholder="e.g. Master Services Agreement"
                value={clientDocName}
                onChange={(e) => setClientDocName(e.target.value)}
                disabled={uploading}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500, color: "#475569", fontSize: "13px" }}>
                Select File
              </label>
              <Upload
                fileList={clientFileList}
                beforeUpload={(file) => {
                  setClientFile(file);
                  setClientFileList([file]);
                  return false;
                }}
                onRemove={() => {
                  setClientFile(null);
                  setClientFileList([]);
                }}
                maxCount={1}
                disabled={uploading}
              >
                <Button icon={<UploadOutlined />} disabled={uploading}>Choose File</Button>
              </Upload>
            </div>
          </div>
        </Modal>

        {/* Candidate Upload Modal */}
        <Modal
          title={
            <span style={{ fontWeight: 700, fontSize: "16px", color: "#1e293b" }}>
              Upload Document for {activeCandidateInfo?.label}
            </span>
          }
          open={candidateModalOpen}
          onCancel={() => {
            setCandidateModalOpen(false);
            setCandidateDocName("");
            setCandidateFile(null);
            setCandidateFileList([]);
          }}
          onOk={handleUploadCandidate}
          okText="Upload"
          cancelText="Cancel"
          okButtonProps={{ className: "btn-premium", loading: uploading }}
          destroyOnClose
        >
          <div style={{ padding: "12px 0" }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500, color: "#475569", fontSize: "13px" }}>
                Document Display Name
              </label>
              <Input
                placeholder="e.g. Resume or Certification"
                value={candidateDocName}
                onChange={(e) => setCandidateDocName(e.target.value)}
                disabled={uploading}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500, color: "#475569", fontSize: "13px" }}>
                Select File
              </label>
              <Upload
                fileList={candidateFileList}
                beforeUpload={(file) => {
                  setCandidateFile(file);
                  setCandidateFileList([file]);
                  return false;
                }}
                onRemove={() => {
                  setCandidateFile(null);
                  setCandidateFileList([]);
                }}
                maxCount={1}
                disabled={uploading}
              >
                <Button icon={<UploadOutlined />} disabled={uploading}>Choose File</Button>
              </Upload>
            </div>
          </div>
        </Modal>

      </div>
    </div>
  );
}
